// shared-ui/DateScope.tsx · v12.10.14 (#1605 #1611 #1621 - Brain\Controls\DATE-CONTROL v1.2, LOCKED 10/11 S219; prototype
//   UI-PROTO-DATE-CONTROL-B1 v1.4): no "Custom" label - date · range · before · after right under the quick picks; one date box
//   for date / before / after; Apply + Cancel always (before Year); Year | Quarter on ONE line, Month in two lines - always shown,
//   in every mode; a Year / Quarter / Month pick fills the date for the mode (range = the period, date and before = the first
//   day, after = the last day). Default mode = the screen's (`defaultMode`, unset = date). `embedded` = inside the phone filter
//   sheet: no own Apply / Cancel (the sheet's Cancel / Show results act for it, `onChange` gets every change) and no
//   Year | Quarter / Month rows (S220, S221). One look on every host - no page may restyle it.
// shared-ui/DateScope.tsx · v12.10.13 (#1566 #1579, REPORT_STANDARD v1.0 §2 / UI-PROTO-REPORTS-B1 v1.6 LOCKED): ONE shared date control -
//   quick picks Today · Yesterday · Last 7D 15D 30D on one line; Custom = range · before · after, the dates, a wide Apply and a
//   normal Cancel; then Year (3 years), Month (two lines) and Quarter (one line). Each screen sets what it allows (`allow`: past |
//   future | both, `yearsBack`); dates outside it are greyed. `QuickPicks` puts the same quick picks in a host's own bar (Reports).
// shared-ui/DateScope.tsx · v12.10.8 (#1204 Custom always offers this year and its months - the rows were hidden until a year had data) · 2026-10-05
// shared-ui/DateScope.tsx · v12.10.6 (#1153 one date picker for T2 Messages, the Call Analyzer and the Cost Analyzer) · 2026-10-04 · VoiceSpectrum AI
//
// THE "DATE SCOPE" CONTROL of T2 Messages (MessagesPage aw-date-panel), lifted out so every screen uses the same one
// (BDR-016 shared screens; Tushar 10/3: "we need to use the same control for call analyzer and cost analyzer").
//   · presets Today / Last 7 days / Last 15 days / Last 30 days - apply at once
//   · Custom: range / before / after - before and after take ONE date (S11.1), a range two; Apply
//   · Custom also has a Year row (one year; only years that have data, up to five back) and a Month row (this year:
//     months up to now); picking a month or a year fills the From / To range.
// It keeps T2's class names (aw-*) so T2 Messages looks exactly as before; a host without T2's stylesheet passes
// `standalone` and gets the same look from dateScope.css.
import { useEffect, useRef, useState } from 'react';
import './dateScope.css';

export type DatePreset = 'Today' | 'Yesterday' | 'Last 7 days' | 'Last 15 days' | 'Last 30 days';
export type DateMode = 'date' | 'range' | 'before' | 'after';
export type DateScope =
  | { kind: 'preset'; value: DatePreset }
  | { kind: 'custom'; mode: DateMode; from?: string; to?: string; year?: number | null; month?: number | null; quarter?: number | null };

export const DATE_PRESETS: DatePreset[] = ['Today', 'Yesterday', 'Last 7 days', 'Last 15 days', 'Last 30 days'];
/** The short words the quick picks show (v12.10.13): Today · Yesterday · Last 7D 15D 30D. */
export const PRESET_SHORT: Record<DatePreset, string> = { Today: 'Today', Yesterday: 'Yesterday', 'Last 7 days': '7D', 'Last 15 days': '15D', 'Last 30 days': '30D' };
export type DateAllow = 'past' | 'future' | 'both';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s?: string) => { if (!s) return null; const [y, m, d] = s.split('-').map(Number); return y && m && d ? new Date(y, m - 1, d) : null; };
const md = (s?: string) => { const d = parse(s); return d ? `${d.getMonth() + 1}/${d.getDate()}` : ''; };
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "Fri 10/09" - one named day (#1611 / #1621). */
export const dayName = (s?: string) => { const d = parse(s); return d ? `${WD[d.getDay()]} ${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}` : ''; };

/** The button words (#1611): "Last 7 days", "Fri 10/09", "Oct 2026", "Q3 2026", "Year 2026", "9/1 - 9/7", "Before 10/1", "After 9/15". */
export function dateScopeLabel(s: DateScope): string {
  if (s.kind === 'preset') return s.value;
  if (s.mode === 'date') return s.from ? dayName(s.from) : 'Pick a date';
  if (s.mode === 'range' && s.year && s.quarter) return `Q${s.quarter} ${s.year}`;
  if (s.mode === 'range' && s.year && s.month != null) return `${MONTHS[s.month]} ${s.year}`;
  if (s.mode === 'range' && s.year) return `Year ${s.year}`;
  if (s.mode === 'range') return s.from && s.to ? `${md(s.from)} - ${md(s.to)}` : s.from ? `From ${md(s.from)}` : 'Date range';
  if (s.mode === 'before') return s.from ? `Before ${md(s.from)}` : 'Before date';
  return s.from ? `After ${md(s.from)}` : 'After date';
}

/** Calendar days (yyyy-MM-dd, inclusive) a scope covers. `first` = the first day with data (before X starts there);
 *  `bucket` tells a chart a whole year or a whole month was picked. */
export function dateScopeRange(s: DateScope, today: Date = new Date(), first?: string): { from: string; to: string; bucket?: 'year' | 'month' } {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const back = (n: number) => { const d = new Date(t); d.setDate(d.getDate() - n); return d; };
  const firstDay = first ?? ymd(new Date(t.getFullYear() - 5, 0, 1));
  if (s.kind === 'preset') {
    if (s.value === 'Yesterday') return { from: ymd(back(1)), to: ymd(back(1)) };
    const n = s.value === 'Today' ? 1 : s.value === 'Last 30 days' ? 30 : s.value === 'Last 15 days' ? 15 : 7;
    return { from: ymd(back(n - 1)), to: ymd(t) };
  }
  if (s.mode === 'date') { const d = s.from || ymd(t); return { from: d, to: d }; }
  if (s.mode === 'before') { const d = parse(s.from); if (!d) return { from: firstDay, to: ymd(t) }; d.setDate(d.getDate() - 1); return { from: firstDay, to: ymd(d) }; }
  if (s.mode === 'after') { const d = parse(s.from); if (!d) return { from: firstDay, to: ymd(t) }; d.setDate(d.getDate() + 1); return { from: ymd(d), to: ymd(t) }; }
  const bucket = s.year && s.month != null ? 'month' as const : s.year && !s.quarter ? 'year' as const : undefined;
  const from = s.from || firstDay;
  const to = s.to && s.to < ymd(t) ? s.to : ymd(t);
  return { from: from <= to ? from : to, to, bucket };
}

export interface DateScopePanelProps {
  value: DateScope;
  onApply: (s: DateScope) => void;
  /** Close without applying (the Cancel button). */
  onCancel?: () => void;
  /** Years that have data, newest first. The current year is always offered; `yearsBack` limits the row (default 3). */
  years?: number[];
  yearsBack?: number;
  /** What the screen allows (REPORT_STANDARD §2): past only (default), future only, or both. Dates outside are greyed. */
  allow?: DateAllow;
  /** A host without T2's stylesheet: draw the panel with dateScope.css. */
  standalone?: boolean;
  title?: string;
  /** Hide the quick picks (Support Ops Click Analyzer only; Reports shows the full control - #1621). */
  hidePresets?: boolean;
  /** The mode the control opens on when nothing custom is picked yet (DATE-CONTROL §1.7): unset = date. */
  defaultMode?: DateMode;
  /** Inside the phone filter sheet (#1612, S220/S221): no own Apply / Cancel, no Year | Quarter / Month rows; every change goes to onChange. */
  embedded?: boolean;
  onChange?: (s: DateScope) => void;
}

/** The quick picks on ONE line: Today · Yesterday · Last 7D 15D 30D (Messages panel, Reports bar). */
export function QuickPicks({ value, onPick, className }: { value: DateScope | null; onPick: (p: DatePreset) => void; className?: string }) {
  const on = (p: DatePreset) => value?.kind === 'preset' && value.value === p;
  const btn = (p: DatePreset) => (
    <button key={p} type="button" className={`aw-chip vs-qp ${on(p) ? 'aw-selected' : ''}`} aria-pressed={on(p)} title={p} onClick={() => onPick(p)}>{PRESET_SHORT[p]}</button>
  );
  return (
    <span className={'vs-qp-row' + (className ? ' ' + className : '')} role="group" aria-label="Quick dates">
      {btn('Today')}{btn('Yesterday')}<span className="vs-qp-last">Last</span>{btn('Last 7 days')}{btn('Last 15 days')}{btn('Last 30 days')}
    </span>
  );
}

/** The panel itself - the one locked control (DATE-CONTROL v1.2). */
export function DateScopePanel({ value, onApply, onCancel, years, yearsBack = 3, allow = 'past', standalone, title = 'Date scope', hidePresets, defaultMode = 'date', embedded, onChange }: DateScopePanelProps) {
  const [custom, setCustomRaw] = useState<Extract<DateScope, { kind: 'custom' }>>(
    value.kind === 'custom' ? value : { kind: 'custom', mode: defaultMode, from: '', to: '', year: null, month: null, quarter: null });
  const touched = useRef(false);
  const setCustom = (f: (c: Extract<DateScope, { kind: 'custom' }>) => Extract<DateScope, { kind: 'custom' }>) => { touched.current = true; setCustomRaw(f); };
  // embedded (phone sheet): every change goes to the sheet's draft - the sheet's Show results applies it
  useEffect(() => { if (embedded && onChange && touched.current && (custom.from || custom.to)) onChange(custom); }, [custom]);   // eslint-disable-line react-hooks/exhaustive-deps
  const today = new Date();
  const ty = today.getFullYear();
  // v12.10.8 (#1204): the current year is always offered. v12.10.13: `yearsBack` years (3), future years only when allowed.
  const ys = (allow === 'future' ? [ty, ty + 1, ty + 2] : [ty, ...(years ?? []), ...Array.from({ length: yearsBack }, (_, i) => ty - i)])
    .filter((y, i, a) => a.indexOf(y) === i && (allow !== 'past' || y <= ty) && y >= ty - (yearsBack - 1))
    .sort((a, b) => b - a).slice(0, Math.max(1, yearsBack));
  const two = custom.mode === 'range';
  const y0 = custom.year ?? ty;
  /** a month is greyed when it lies wholly outside what the screen allows */
  const monthOff = (y: number, m: number) => allow === 'past' ? (y > ty || (y === ty && m > today.getMonth())) : allow === 'future' ? (y < ty || (y === ty && m < today.getMonth())) : false;
  const quarterOff = (y: number, q: number) => [0, 1, 2].every((k) => monthOff(y, (q - 1) * 3 + k));
  const clip = (from: Date, to: Date) => {
    let f = from, t = to;
    if (allow === 'past' && t > today) t = today;
    if (allow === 'future' && f < today) f = today;
    return { from: ymd(f), to: ymd(t) };
  };
  /** DATE-CONTROL §1.6 - a Year / Quarter / Month pick fills the date box for the picked mode (the mode stays). */
  const fill = (mode: DateMode, y: number, m: number | null, q: number | null) => {
    const start = m != null ? new Date(y, m, 1) : q ? new Date(y, (q - 1) * 3, 1) : new Date(y, 0, 1);
    const end = m != null ? new Date(y, m + 1, 0) : q ? new Date(y, q * 3, 0) : new Date(y, 11, 31);
    if (mode === 'range') return clip(start, end);
    if (mode === 'after') return { from: ymd(end), to: '' };
    return { from: ymd(start), to: '' };   // date, before = the first day
  };
  const pick = (y: number, m: number | null, q: number | null) =>
    setCustom((c) => ({ kind: 'custom', mode: c.mode, year: y, month: m, quarter: q, ...fill(c.mode, y, m, q) }));
  const pickMode = (mode: DateMode) => setCustom((c) => c.year
    ? { ...c, mode, ...fill(mode, c.year, c.month ?? null, c.quarter ?? null) }
    : { ...c, mode, to: mode === 'range' ? c.to : '' });
  const setDate = (k: 'from' | 'to', v: string) => setCustom((c) => ({ ...c, [k]: v, year: null, month: null, quarter: null }));
  const lim = { max: allow === 'past' ? ymd(today) : undefined, min: allow === 'future' ? ymd(today) : undefined };
  const boxLabel = custom.mode === 'before' ? 'Before date' : custom.mode === 'after' ? 'After date' : custom.mode === 'date' ? 'Date' : 'Start date';

  return (
    <div className={'aw-date-panel vs-ds' + (standalone ? ' vs-ds-own' : '') + (embedded ? ' vs-ds-emb' : '')} role="dialog" aria-label={title}>
      {!hidePresets && (<>
        <h2 className="aw-panel-title">{title}</h2>
        <div className="aw-preset-row vs-ds-quick">
          <QuickPicks value={value} onPick={(p) => { if (embedded && onChange) onChange({ kind: 'preset', value: p }); else onApply({ kind: 'preset', value: p }); }} />
        </div>
      </>)}
      <div className={'aw-custom-date vs-ds-c' + (hidePresets ? ' vs-ds-nopre' : '')}>
        <div className="aw-mode-row vs-ds-modes" role="group" aria-label="Pick dates by">
          {(['date', 'range', 'before', 'after'] as const).map((m) => (
            <button key={m} type="button" className={`aw-chip ${custom.mode === m ? 'aw-selected' : ''}`} aria-pressed={custom.mode === m} onClick={() => pickMode(m)}>{m}</button>
          ))}
        </div>
        <div className="aw-custom-inputs vs-ds-dates">
          <input type="date" aria-label={boxLabel} {...lim} value={custom.from ?? ''} onChange={(e) => setDate('from', e.target.value)} />
          {two && <><span className="vs-ds-to">to</span><input type="date" aria-label="End date" {...lim} value={custom.to ?? ''} onChange={(e) => setDate('to', e.target.value)} /></>}
        </div>
        {!embedded && (
          <div className="vs-ds-foot">
            <button className="aw-primary vs-ds-apply" type="button" onClick={() => onApply(custom)}>Apply</button>
            <button className="aw-secondary vs-ds-cancel" type="button" onClick={() => onCancel?.()}>Cancel</button>
          </div>
        )}
        {!embedded && (<>
          <div className="vs-ds-sub vs-ds-yqcap"><span>Year</span><span>Quarter</span></div>
          <div className="vs-ds-yq">
            <span className="vs-ds-years">
              {ys.map((y) => <button key={y} type="button" className={`aw-chip ${custom.year === y ? 'aw-selected' : ''}`} onClick={() => pick(y, null, null)}>{y}</button>)}
            </span>
            <span className="vs-ds-sep" aria-hidden="true" />
            <span className="vs-ds-quarters">
              {[1, 2, 3, 4].map((q) => (
                <button key={q} type="button" className={`aw-chip ${custom.quarter === q ? 'aw-selected' : ''}`}
                  disabled={quarterOff(y0, q)} onClick={() => pick(y0, null, q)}>Q{q}</button>
              ))}
            </span>
          </div>
          <div className="vs-ds-sub">Month <span>- fills the date</span></div>
          <div className="vs-ds-months">
            {MONTHS.map((m, i) => (
              <button key={m} type="button" className={`aw-chip ${custom.month === i ? 'aw-selected' : ''}`}
                disabled={monthOff(y0, i)} title={monthOff(y0, i) ? 'Not available on this screen' : undefined} onClick={() => pick(y0, i, null)}>{m}</button>
            ))}
          </div>
        </>)}
      </div>
    </div>
  );
}

/** The chip + panel together, for screens that do not have T2's own date chip (the analyzers). */
export function DateScopeButton(props: DateScopePanelProps & { label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className="vs-ds-wrap" ref={ref}>
      <button type="button" className="vs-ds-chip" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span aria-hidden="true">📅</span> {props.label ?? dateScopeLabel(props.value)} ▾
      </button>
      {open && <DateScopePanel {...props} standalone onCancel={() => setOpen(false)} onApply={s => { props.onApply(s); setOpen(false); }} />}
    </div>
  );
}
